/* @flow */
import * as React from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import querystring from "querystring";
import moment from "moment";
import memoize from "lodash/memoize";

import type { State } from "../../redux/reducer";
import type { Dispatch } from "../../redux/action";

const Direction = styled.span`
  direction: ${props => props.direction || "ltr"};
`;

const Tomato = styled.span`
  background-color: ${process.env.REACT_APP_STATIC_RENDER === "true"
    ? "transparent"
    : "tomato"};
  direction: ltr;
`;

function lookupString(dictionary, string) {
  return dictionary[string] || null;
}

function lookupSubstrings(dictionary, substrings: Array<string>) {
  const key = substrings.join("***");
  const translation = lookupString(dictionary, key);

  if (typeof translation === "string") {
    return translation.split("***");
  }

  // Обязательно возвращаем те же самые
  // подстроки, чтобы потом можно было
  // сравнивать массивы с `===`
  return substrings;
}

const defaultLocale = "en";
const defaultDictionary = {};

type MinimalDataToInferLocale = {
  profile: $PropertyType<State, "profile">,
  serverData: $PropertyType<State, "serverData">,
  fallbackLocale: $PropertyType<State, "fallbackLocale">
};

export function inferLocale({
  profile,
  serverData,
  fallbackLocale
}: MinimalDataToInferLocale) {
  const query = querystring.parse(
    window.encodeURI(window.location.search.slice(1))
  );

  let userLang =
    window.navigator.language ||
    window.navigator.userLanguage ||
    window.navigator.browserLanguage ||
    window.navigator.systemLanguage;
  userLang = userLang.split("-")[0];
  if (userLang === "vi") userLang = "vn";
  if (userLang === "tl") userLang = "ph";
  if (userLang === "fil") userLang = "ph";

  const notApp =
    serverData != null && !serverData.isIOS && !serverData.isAndroidApp;

  if (query.lang != null) {
    return query.lang;
  } else if (profile != null) {
    return profile.locale;
  } else if (fallbackLocale != null) {
    return fallbackLocale;
  } else if (!process.env.REACT_APP_DEPLOY_TARGET === "siamoption" && localeMap[userLang] && notApp) {
    return userLang;
  } else if (process.env.REACT_APP_DEPLOY_TARGET === "siamoption" && siamLocaleMap[userLang] && notApp) {
    return userLang;
  } else if (serverData != null) {
    return serverData.lang;
  } else {
    return defaultLocale;
  }
}
const siamLocaleMap = {
  en: "en",
  th: "th"
};

const localeMap = {
  en: "en",
  // jp: "jv",
  ph: "tl-ph",
  th: "th",
  hi: "hi",
  id: "id",
  vn: "vi",
  ar: "ar"
};
export function inferMomentLocale(state: State) {
  const locale = inferLocale(state);

  return localeMap[locale] || "en";
}

function mapStateToDictionary(state: State) {
  const locale = inferLocale(state);

  return {
    locale,
    dictionary: state.dictionaries[locale]
  };
}

let makeGettext = memoize(dictionary => {
  return (strings: Array<string> | string, ...values: Array<any>) => {
    if (typeof strings === "string") {
      if (process.env.NODE_ENV === "development") {
        try {
          throw new TypeError(
            "gettext(string) is deprecated, please use gettext with template literal"
          );
        } catch (error) {
          console.warn(error.message + "\n" + error.stack.split("\n")[2]);
        }
      }

      return lookupString(dictionary, strings) || strings;
    }

    const translations = lookupSubstrings(dictionary, strings);

    return translations.reduce(
      (result, string, index) =>
        result + string + (values.length > index ? values[index] : ""),
      ""
    );
  };
});

export type WithGettext = (key: React.Node | Array<React.Node>) => React.Node;

export function mapStateToGettext(state: State) {
  const { dictionary = defaultDictionary } = mapStateToDictionary(state);

  return {
    gettext: makeGettext(dictionary),
    isGettextReady: dictionary !== defaultDictionary
  };
}

export const withGettext = connect(mapStateToGettext);

type GettextProps = {
  children: string | Array<string | React.Node>,
  dictionary: Object,
  locale: string
};

export function getDirection(locale: string): string {
  switch (locale) {
    case "ar":
      return "rtl";
    default:
      return "ltr";
  }
}

function UnidirectionalText(props) {
  return (
    <Direction direction={getDirection(props.locale)}>
      {props.children}
    </Direction>
  );
}

function Gettext(props: GettextProps) {
  const { dictionary = defaultDictionary } = props;

  if (Array.isArray(props.children)) {
    const strings = [];
    const values = [];

    props.children.forEach((child, index) => {
      if (index % 2 === 0) {
        strings.push(typeof child === "string" ? child : "");
      } else {
        values.push(child);
      }
    });

    // Строк всегда на 1 больше, чем значений,
    // чтобы последнее значение не потерялось.
    if (strings.length === values.length) {
      strings.push("");
    }

    const translation = lookupSubstrings(dictionary, strings);

    if (process.env.NODE_ENV === "development") {
      if (translation === strings) {
        return translation.reduce(
          (result, string, index) => [
            ...result,
            <Tomato key={index}>{string}</Tomato>,
            values[index]
          ],
          []
        );
      }
    }

    return (
      <UnidirectionalText locale={props.locale}>
        {translation.reduce(
          (result, string, index) => [...result, string, values[index]],
          []
        )}
      </UnidirectionalText>
    );
  }

  if (typeof props.children === "string") {
    const translation = lookupString(dictionary, props.children);

    if (typeof translation === "string") {
      return (
        <UnidirectionalText locale={props.locale}>
          {translation}
        </UnidirectionalText>
      );
    }

    if (
      process.env.NODE_ENV === "development" &&
      typeof props.children === "string"
    ) {
      // Повторная typeof проверка нужна чтобы flow
      // не ругалась. Мы не можем гарантировать ей,
      // что lookupString() блоком выше не изменил
      // тип props.children.

      return <Tomato>{props.children}</Tomato>;
    }

    return <Direction>{props.children}</Direction>;
  }

  throw new TypeError("<Gettext/> expects a strng or an array as `children`");
}

export default connect(mapStateToDictionary)(Gettext);

type DictionaryUpdaterProps = {
  fetchDictionary: (locale: string) => Promise<Object>,
  locale: string,
  dictionary: Object,
  dispatch: Dispatch
};

class StatelessDictionaryUpdater extends React.Component<DictionaryUpdaterProps> {
  UNSAFE_componentWillMount() {
    this.props.dispatch({
      type: "REQUEST_DICTIONARY",
      payload: this.props.locale
    });
  }

  componentDidMount() {
    const { locale } = this.props;

    this.props.fetchDictionary(locale).then(dictionary => {
      this.props.dispatch({
        type: "RECEIVE_DICTIONARY",
        payload: { dictionary, locale }
      });
      moment.locale(localeMap[locale]);
    });
  }

  render() {
    return null;
  }
}

export const DictionaryUpdater = connect(mapStateToDictionary)(props => (
  <StatelessDictionaryUpdater key={props.locale} {...props} />
));



// WEBPACK FOOTER //
// ./src/components/common/Gettext.js